var _a, _b;
import { getHostName } from '../utils/getHostingApp';
import { getRequestHeaders } from '../utils/getRequestHeaders';
import { IMPLEMENTATION_ENDPOINTS } from '../constants/urls';
import analytics from '../analytics/analytics';
/**
 * These constants are used in both the ConfigProvider and the ConfigContext.
 */
export const IS_DEV = process.env.NODE_ENV === 'development';
export const IS_STORYBOOK = process.env.STORYBOOK_ENV;
export const IS_CHROMATIC = typeof window !== 'undefined' && ((_b = (_a = location.href) === null || _a === void 0 ? void 0 : _a.match(/chromatic/)) === null || _b === void 0 ? void 0 : _b.length) > 0;
const host = getHostName(IS_STORYBOOK, IS_CHROMATIC);
export const IS_DEBUG = false;
export const PERSONALIZED_GREETING_URL = `${host}/genai-svc/genai/personalization/get-personalized-greeting`;
export const RETRIEVE_URL = `${host}/genai-svc/genai/chat/retrieve-prompts`;
export const SPEECH_URL = `${host}/genai-svc/genai/speech`;
export const SPEECH_TIMEOUT = 10000;
export const TRANSCRIPTION_URL = `${host}/genai-svc/genai/audio/transcriptions`;
export const TRANSCRIPTION_TIMEOUT = 10000;
/**
 * This is the serviceConfig for each viewType.
 * New viewTypes should be added here.
 */
export const getServiceConfig = (viewType, isMobile, customerName = '', logAnalytics) => {
  const sameForAll = {
    personalizedGreetingUrl: PERSONALIZED_GREETING_URL,
    retrieveUrl: RETRIEVE_URL,
    speechFetchRequestOptions: {
      url: SPEECH_URL,
      timeout: SPEECH_TIMEOUT
    },
    transcriptionFetchRequestOptions: {
      url: TRANSCRIPTION_URL,
      timeout: TRANSCRIPTION_TIMEOUT
    }
  };
  const serviceConfig = {
    LANDING: Object.assign({
      customerName,
      debugMode: IS_DEV,
      fetchRequestOptions: {
        url: `${host}${IMPLEMENTATION_ENDPOINTS.LANDING}`,
        headers: getRequestHeaders(isMobile, {
          'x-request-from': 'homePage'
        })
      },
      ga4ProductName: '',
      isDev: IS_DEV,
      requestFrom: 'homePage',
      returnJSONResponse: true,
      showLandingChat: true,
      viewType
    }, sameForAll),
    CHECKOUT: Object.assign({
      customerName,
      debugMode: IS_DEV,
      fetchRequestOptions: {
        url: `${host}${IMPLEMENTATION_ENDPOINTS.CHECKOUT}`,
        headers: getRequestHeaders(isMobile, {
          'x-request-from': 'UC'
        })
      },
      ga4ProductName: '',
      isDev: IS_DEV,
      requestFrom: 'UC',
      returnJSONResponse: true,
      showLandingChat: false,
      viewType
    }, sameForAll),
    DRIVE: Object.assign({
      customerName,
      debugMode: IS_DEV,
      fetchRequestOptions: {
        url: `${host}${IMPLEMENTATION_ENDPOINTS.DRIVE}`,
        headers: getRequestHeaders(isMobile, {
          'x-request-from': 'rcListingPage'
        })
      },
      ga4ProductName: 'car',
      isDev: IS_DEV,
      requestFrom: 'rcListingsPage',
      returnJSONResponse: true,
      showLandingChat: false,
      viewType
    }, sameForAll),
    FLY: Object.assign({
      customerName,
      debugMode: IS_DEV,
      fetchRequestOptions: {
        url: `${host}${IMPLEMENTATION_ENDPOINTS.FLY}`,
        headers: getRequestHeaders(isMobile, {
          'x-request-from': 'flightListingPage'
        })
      },
      ga4ProductName: 'air',
      isDev: IS_DEV,
      requestFrom: 'flightListingPage',
      returnJSONResponse: true,
      showLandingChat: false,
      viewType
    }, sameForAll),
    ITINERARY: Object.assign({
      customerName,
      debugMode: IS_DEV,
      fetchRequestOptions: {
        url: `${host}${IMPLEMENTATION_ENDPOINTS.ITINERARY}`,
        headers: getRequestHeaders(isMobile, {
          'x-request-from': 'travel-itinerary'
        })
      },
      ga4ProductName: '',
      isDev: IS_DEV,
      requestFrom: 'travel-itinerary',
      showLandingChat: false,
      viewType
    }, sameForAll),
    // TODO: add package service config
    // PACKAGE: {
    //   customerName,
    //   debugMode: IS_DEV,
    //   ga4ProductName: 'bundle',
    //   isDev: IS_DEV,
    //   viewType,
    //   showLandingChat: false,
    //   requestFrom: '',
    //   returnJSONResponse: true,
    //   fetchRequestOptions: {},
    //   ...sameForAll,
    // },
    PORTAL: Object.assign({
      customerName,
      debugMode: IS_DEV,
      ga4ProductName: '',
      isDev: IS_DEV,
      viewType,
      showLandingChat: false,
      returnJSONResponse: true,
      requestFrom: 'pennyPortalPage',
      fetchRequestOptions: {
        url: `${host}${IMPLEMENTATION_ENDPOINTS.PORTAL}`,
        headers: {
          'x-request-from': 'pennyPortalPage',
          'x-model-version': '4'
        }
      }
    }, sameForAll),
    STAYDETAILS: Object.assign({
      customerName,
      debugMode: IS_DEV,
      ga4ProductName: 'hotel',
      isDev: IS_DEV,
      viewType,
      showLandingChat: false,
      requestFrom: 'hotelDetailPage',
      returnJSONResponse: true,
      fetchRequestOptions: {
        url: `${host}${IMPLEMENTATION_ENDPOINTS.STAYDETAILS}`,
        headers: getRequestHeaders(isMobile, {
          'x-request-from': 'hotelDetailPage'
        })
      }
    }, sameForAll),
    STAYLISTINGS: Object.assign({
      customerName,
      debugMode: IS_DEV,
      ga4ProductName: 'hotel',
      isDev: IS_DEV,
      viewType,
      showLandingChat: false,
      requestFrom: 'hotelListingPage',
      returnJSONResponse: true,
      fetchRequestOptions: {
        url: `${host}${IMPLEMENTATION_ENDPOINTS.STAYLISTINGS}`,
        headers: getRequestHeaders(isMobile, {
          'x-request-from': 'hotelListingPage'
        })
      }
    }, sameForAll)
  };
  // Ensure viewType has a valid service config
  const config = serviceConfig[viewType];
  if (!config) {
    throw new Error(`No service configuration found for viewType: ${viewType}. ` + 'Add configuration to serviceConfig object in /react-components/penny-chat-bot/src/config/serviceConfig.ts. ');
  }
  // Add logAnalytics after serviceConfig is defined
  const resolvedConfig = Object.assign(Object.assign({}, config), {
    logAnalytics: Object.assign({
      logCustomerCarePhoneNumberClicked: () => {},
      logCustomerCarePhoneNumberDisplayed: () => {},
      logChatInitialization: () => {
        analytics.fireGA4Event({
          event: 'initiate_chat',
          attributes: {
            type: 'penny',
            page_name: config.requestFrom,
            product_name: config.ga4ProductName
          }
        });
      },
      logFirstMessageSubmitted: chatMessages => {
        if (chatMessages.filter(m => m.role === 'user' && m.content !== '').length <= 1) {
          analytics.fireGA4Event({
            event: 'begin_chat',
            attributes: {
              type: 'penny',
              page_name: config.requestFrom,
              product_name: config.ga4ProductName
            }
          });
        }
      }
    }, logAnalytics)
  });
  return resolvedConfig;
};